import { AggregateDisplay } from "~/components/AggregateDisplay";
import { TimeSeriesItem } from "~/modules/token_timeseries/types";
import { QueryResponse } from "~/modules/filters/types";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import {
    getLastTimeseriesValue,
    getTimeseriesValue30DAgo,
} from "~/utils/getLastTimeseriesValue";

// // // //

/**
 * Broadridge Individual Aggregates Component
 * Displays key metrics for Broadridge platform including treasury processing and participant data
 */
export function BroadridgeIndividualAggregates(props: {
    totalContractsTimeseries: QueryResponse<TimeSeriesItem>;
    totalCollateralizedFundsTimeseries: QueryResponse<TimeSeriesItem>;
    totalIssuedFundsTimeseries: QueryResponse<TimeSeriesItem>;
}) {
    const {
        totalContractsTimeseries,
        totalCollateralizedFundsTimeseries,
        totalIssuedFundsTimeseries,
    } = props;

    // Calculate Tokenized Treasury Notional from totalCollateralizedFundsTimeseries
    const tokenizedTreasuryNotional = getLastTimeseriesValue(
        totalCollateralizedFundsTimeseries.results[0],
    );
    const tokenizedTreasuryNotional30DAgo = getTimeseriesValue30DAgo(
        totalCollateralizedFundsTimeseries.results[0],
    );
    const tokenizedTreasuryPercentChange = calculatePercentChange({
        initialValue: tokenizedTreasuryNotional30DAgo,
        finalValue: tokenizedTreasuryNotional,
    });

    // Calculate Tokenized Payments from totalIssuedFundsTimeseries
    const tokenizedPaymentsProcessed = getLastTimeseriesValue(
        totalIssuedFundsTimeseries.results[0],
    );
    const tokenizedPaymentsProcessed30DAgo = getTimeseriesValue30DAgo(
        totalIssuedFundsTimeseries.results[0],
    );
    const tokenizedPaymentsPercentChange = calculatePercentChange({
        initialValue: tokenizedPaymentsProcessed30DAgo,
        finalValue: tokenizedPaymentsProcessed,
    });

    // Calculate Smart Contracts from totalContratsTimeseries
    const totalSmartContracts = getLastTimeseriesValue(
        totalContractsTimeseries.results[0],
    );
    const totalSmartContracts30DAgo = getTimeseriesValue30DAgo(
        totalContractsTimeseries.results[0],
    );
    const smartContractsPercentChange = calculatePercentChange({
        initialValue: totalSmartContracts30DAgo,
        finalValue: totalSmartContracts,
    });

    // For participants, use smart contracts data as a proxy until proper data is available
    // This is a temporary implementation
    // const totalParticipants = Math.floor(totalSmartContracts / 1000); // Rough conversion
    // const totalParticipants30DAgo = Math.floor(
    //     totalSmartContracts30DAgo / 1000,
    // );
    // const participantsPercentChange = calculatePercentChange({
    //     initialValue: totalParticipants30DAgo,
    //     finalValue: totalParticipants,
    // });

    return (
        <div className="col-span-1 lg:col-span-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 mt-6">
            <AggregateDisplay
                label="Daily Repo Par Value"
                tooltip="Aggregate face value of all securities used as collateral in repo transactions on a given day. For a Treasury repo, this is the par amount of Treasuries delivered, not their market value or the cash borrowed."
                type="dollar_compact"
                value={tokenizedTreasuryNotional}
                percentChange={{
                    value: tokenizedTreasuryPercentChange,
                    interval: "30d",
                }}
            />

            <AggregateDisplay
                label="Daily Repo Turnover"
                tooltip="Total Aggregate cash volume exchanged in repo transactions on a given day. The sum of all cash lent or borrowed. For overnight repos, this equals the principal amount; for term repos, the initial cash leg."
                type="dollar_compact"
                value={tokenizedPaymentsProcessed}
                percentChange={{
                    value: tokenizedPaymentsPercentChange,
                    interval: "30d",
                }}
            />

            <AggregateDisplay
                label="Daily Repo Trade Count"
                tooltip="Count of individual repurchase agreement transactions settled on a given day. Includes both new trades and rolls. One trade = one counterparty pair agreeing to exchange securities for cash with a repurchase obligation."
                type="count"
                value={totalSmartContracts}
                percentChange={{
                    value: smartContractsPercentChange,
                    interval: "30d",
                }}
            />

            {/* <AggregateDisplay
                label="Participants"
                tooltip="Financial institutions actively participating"
                type="count"
                value={totalParticipants}
                percentChange={{
                    value: participantsPercentChange,
                    interval: "30d",
                }}
            /> */}
        </div>
    );
}
